import React, { useState, useEffect, useRef } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import styles from "../Settings.module.scss";
import { ActiveCell, SignatureCell, EditCommandCell } from "../GridComponents";
import SearchTextBox from "components/SearchComponent";
import UserProfileForm from "./UserProfileForm";
import Modal from "../Modal";

import {
  useUsers,
  addUser,
  updateUser,
  getUserInfo,
} from "services/userService";
import { getProfilePhoto, clearProfilePhoto } from "actions/currentuser.action";

const LoadingPanel = () => {
  return (
    <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );
};

const AdminCell = (props) => (
  <td className={`${styles[`checkbox`] + " " + styles[`marginLeft`]}`}>
    <Checkbox checked={!!props.dataItem.isAdmin} readOnly />
  </td>
);

const FeeearnerCell = (props) => (
  <td className={`${styles[`checkbox`] + " " + styles[`marginLeft`]}`}>
    <Checkbox checked={!!props.dataItem.isFeeEarner} readOnly />
  </td>
);

const NameCell = (props) => {
  const { dataItem } = props;
  return (
    <td>
      <div className="d-flex flex-column">
        <div>
          <span>{dataItem.fullName}</span>
        </div>
        <div className={styles[`addressLines`] + " " + styles[`pointer`]}>
          <u onClick={() => props.enterEdit(dataItem, "View")}>
            {dataItem.userRef}
          </u>
        </div>
      </div>
    </td>
  );
};

const UsersList = (props) => {
  const dispatch = useDispatch();

  const { data: users, loading } = useUsers();
  const [products, setProducts] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [addNewForm, setAddNewForm] = useState(false);
  const [modalValue, setModalValue] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchRef = useRef();

  useEffect(() => {
    if (!users) return;

    const items = users.map((item) => {
      const firstName = item.firstName ?? "";
      const lastName = item.lastName ?? "";
      return {
        ...item,
        fullName: `${firstName} ${lastName}`.trim(),
        symbol: firstName || lastName ? firstName[0] || lastName[0] : "?",
      };
    });

    const filtered = searchValue
      ? items.filter(
          (u) =>
            u.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
            u.emailAddress?.toLowerCase().includes(searchValue.toLowerCase())
        )
      : items;

    setProducts(filtered);
  }, [users, searchValue]);

  const enterEdit = async (item, view) => {
    try {
      const userData = await getUserInfo(item.userID);
      const dataToPass = { ...userData, type: view ? "View" : "Edit" };

      ["dateJoined", "dateQualified", "dateOfBirth", "dateLeft"].forEach(
        (field) => {
          if (dataToPass[field]) {
            try {
              dataToPass[field] = new Date(dataToPass[field]);
            } catch {
              dataToPass[field] = null;
            }
          } else {
            dataToPass[field] = null;
          }
        }
      );

      if (dataToPass.userImageDocumentId) {
        dispatch(getProfilePhoto(dataToPass.userImageDocumentId));
      }
      setEditItem(dataToPass);
    } catch (err) {
      toast.error("Failed to load user data");
    } finally {
      setOpenForm(true);
    }
  };

  const handleSubmitAdd = async (event) => {
    try {
      const res = await addUser(event);
      if (res.success) {
        toast.success("New User Added");
      }
    } catch (err) {
      toast.error("Failed to add user");
    } finally {
      setAddNewForm(false);
    }
  };

  const handleSubmitEdit = async (event) => {
    try {
      const res = await updateUser(event.userID, event);
      if (res.success) {
        toast.success("Updated successfully");
      }
    } catch (err) {
      toast.error("Failed to update user");
    } finally {
      setOpenForm(false);
      dispatch(clearProfilePhoto());
    }
  };

  const handleCancelEdit = () => {
    setOpenForm(false);
    dispatch(clearProfilePhoto());
  };

  const MyEditCommandCell = (props) => (
    <EditCommandCell {...props} enterEdit={enterEdit} />
  );

  return (
    <>
      {!addNewForm ? (
        <div className="w-100 mx-auto">
          <div className="d-flex justify-content-between">
            <div className="ps-3 mt-5">
              <SearchTextBox
                ref={searchRef}
                changeFilterValue={(val) => setSearchValue(val)}
              />
            </div>
            <div className="pe-3">
              <Button
                className="common-btn mt-5"
                onClick={()=>{
                  setAddNewForm(true);
                  dispatch(clearProfilePhoto());
                }}
                disabled={loading}
              >
                Add User
              </Button>
            </div>
          </div>

          <div className="row mx-auto1 mt-3 w-100">
            <div className="settings-grid users-grid col-12">
              {loading && <LoadingPanel />}
              <Grid data={products}>
                <Column
                  field="symbol"
                  title=" "
                  cell={SignatureCell}
                  width="80"
                />
                <Column
                  field="fullName"
                  title=" "
                  cell={(props) => (
                    <NameCell {...props} enterEdit={enterEdit} />
                  )}
                  width="200"
                />
                <Column field="jobTitle" title="Job Title" width="220px" />
                <Column field="emailAddress" title="Email" width="280" />
                <Column field="phoneNo" title="Phone No" width="140" />
                <Column cell={FeeearnerCell} title="Is FeeEarner" width="120" />
                <Column cell={AdminCell} title="Admin" width="80" />
                <Column cell={ActiveCell} title="Active" width="80" />
                <Column title="" cell={MyEditCommandCell} width="80" />
              </Grid>
            </div>
          </div>
        </div>
      ) : (
        <UserProfileForm
          mode="add"
          onSubmit={handleSubmitAdd}
          onCancel={() => {
            setAddNewForm(false);
            dispatch(clearProfilePhoto());
          }}
        />
      )}

      <Modal
        value={modalValue}
        display={"User has been added"}
        closeform={() => setModalValue(false)}
        component="User"
      />

      {openForm && (
        <Dialog onClose={handleCancelEdit} width={1150} title="Edit User">
          <UserProfileForm
            mode="edit"
            onSubmit={handleSubmitEdit}
            initialData={editItem}
            onCancel={handleCancelEdit}
          />
        </Dialog>
      )}
    </>
  );
};

export default UsersList;
